import { z } from 'zod';
export const OpsIotDeviceProvisioningStatusSchema = z.enum([
    'provisioned',
    'not_provisioned',
]);
export const OpsIotProvisioningStatusSchema = OpsIotDeviceProvisioningStatusSchema;
/** All hardware categories — shared by ops and tenant device views. */
export const OpsDeviceCategorySchema = z.enum([
    'efem-gateway',
    'location-gateway',
    'location-tag',
    'location-anchor',
    'barcode-scanner',
    'handheld-barcode-scanner',
    'waferid-camera',
]);
export const DeviceInventoryStatusSchema = z.enum([
    'in-stock',
    'allocated',
    'shipped',
    'claimed',
    'active',
    'returned',
    'retired',
]);
export const OpsDeviceStateHistoryEntrySchema = z.object({
    from: OpsIotDeviceProvisioningStatusSchema.or(DeviceInventoryStatusSchema).or(z.literal('created')),
    to: OpsIotDeviceProvisioningStatusSchema.or(DeviceInventoryStatusSchema),
    actor: z.string(),
    timestamp: z.string(),
    reason: z.string().nullable().optional(),
});
export const OpsDeviceRecordSchema = z.object({
    serialNumber: z.string(),
    macAddress: z.string().nullable().optional(),
    ekHash: z.string().nullable().optional(),
    ekCertPem: z.string().nullable().optional(),
    manufacturerName: z.string().nullable().optional(),
    /** Hardware model — was `model` prior to table consolidation */
    deviceModel: z.string().nullable().optional(),
    mfgBatch: z.string().nullable().optional(),
    mfgDate: z.string().nullable().optional(),
    firmwareRevision: z.string().nullable().optional(),
    vendorSerialNumber: z.string().nullable().optional(),
    iotProvisioningStatus: OpsIotDeviceProvisioningStatusSchema.nullable().optional(),
    tenantId: z.string().nullable().optional(),
    /** Site assignment for location-gateway (set via location deployment APIs). */
    siteId: z.string().nullable().optional(),
    siteAssignedAt: z.string().nullable().optional(),
    vendorKey: z.string().nullable().optional(),
    deviceCategory: OpsDeviceCategorySchema.nullable().optional(),
    orderId: z.string().nullable().optional(),
    /** Timestamp when device was allocated to a tenant — was `assignedAt` prior to table consolidation */
    allocatedAt: z.string().nullable().optional(),
    shippedAt: z.string().nullable().optional(),
    claimedAt: z.string().nullable().optional(),
    returnedAt: z.string().nullable().optional(),
    retiredAt: z.string().nullable().optional(),
    thingName: z.string().nullable().optional(),
    certificateId: z.string().nullable().optional(),
    certificateRevokedAt: z.string().nullable().optional(),
    activatedAt: z.string().nullable().optional(),
    deactivatedAt: z.string().nullable().optional(),
    akCertPem: z.string().nullable().optional(),
    reprovisioningPermitted: z.boolean().nullable().optional(),
    ssmInstanceId: z.string().nullable().optional(),
    deviceSigningKeyPub: z.string().nullable().optional(),
    challengeSecretHash: z.string().nullable().optional(),
    challengeExpiresAt: z.string().nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string().nullable().optional(),
    vendorDeviceId: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    /** Actual inventory lifecycle state (in-stock/allocated/shipped/active/returned/retired) */
    inventoryStatus: DeviceInventoryStatusSchema.optional(),
    stateHistory: z.array(OpsDeviceStateHistoryEntrySchema).optional(),
    /** EFEM installation topology (barcode-scanner / waferid-camera) — §3.10 wafer imaging plan */
    efemPeripheral: z.record(z.unknown()).nullable().optional(),
    /** Location installation topology (anchor/tag on location-gateway) — future commissioning */
    locationPeripheral: z.record(z.unknown()).nullable().optional(),
    /** Denormalized parent gateway serial (efem-gateway or location-gateway) for GSI4 */
    parentGatewaySerialNumber: z.string().nullable().optional(),
    /** Commissioned EFEM OEM adapter — see EFEM_ADAPTER_ASSIGNMENT_AND_GREENGRASS.md */
    efemAdapterAssignment: z
        .object({
        efemAdapterKey: z.string(),
        assignedAt: z.string(),
        assignedBy: z.string(),
        notes: z.string().optional(),
    })
        .nullable()
        .optional(),
    /** Commissioned location OEM adapter (UWB engine, etc.) — see gateway-adapter-common */
    locationAdapterAssignment: z
        .object({
        locationAdapterKey: z.string(),
        assignedAt: z.string(),
        assignedBy: z.string(),
        notes: z.string().optional(),
    })
        .nullable()
        .optional(),
});
export const OpsDeviceDetailSchema = OpsDeviceRecordSchema.extend({
    resolvedTenantName: z.string().nullable().optional(),
    resolvedVendorDisplayName: z.string().nullable().optional(),
});
export const NonIotDeviceRecordSchema = z.object({
    serialNumber: z.string(),
    deviceCategory: OpsDeviceCategorySchema,
    manufacturerName: z.string().nullable().optional(),
    /** Hardware model — was `model` prior to table consolidation */
    deviceModel: z.string().nullable().optional(),
    mfgBatch: z.string().nullable().optional(),
    mfgDate: z.string().nullable().optional(),
    createdAt: z.string(),
    macAddress: z.string().nullable().optional(),
    vendorSerialNumber: z.string().nullable().optional(),
    vendorDeviceId: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    ekHash: z.string().nullable().optional(),
    akCertPem: z.string().nullable().optional(),
    hardwareRevision: z.string().nullable().optional(),
    firmwareRevision: z.string().nullable().optional(),
    inventoryStatus: DeviceInventoryStatusSchema.optional(),
    tenantId: z.string().nullable().optional(),
    orderId: z.string().nullable().optional(),
    /** Timestamp when device was allocated to a tenant — was `assignedAt` prior to table consolidation */
    allocatedAt: z.string().nullable().optional(),
    claimCode: z.string().regex(/^[A-Z0-9]{4}-[A-Z0-9]{4}$/).optional(),
    claimCodeExpiresAt: z.string().optional(),
    claimedAt: z.string().nullable().optional(),
    claimedByUserId: z.string().nullable().optional(),
    activatedAt: z.string().nullable().optional(),
    shippedAt: z.string().nullable().optional(),
    returnedAt: z.string().nullable().optional(),
    retiredAt: z.string().nullable().optional(),
    stateHistory: z.array(OpsDeviceStateHistoryEntrySchema).optional(),
    resolvedTenantName: z.string().nullable().optional(),
    /** Denormalized parent gateway serial when bound to an EFEM gateway load port */
    parentGatewaySerialNumber: z.string().nullable().optional(),
});
//# sourceMappingURL=device-inventory.schemas.js.map